import { z } from 'zod';
import { ServiceLineSchema } from './opportunities';
import { TimeEntryPhaseSchema } from './time-entries';
/**
 * Gastos (facturas recibidas y notas de gasto) de JR2.
 *
 * Importes en CÉNTIMOS enteros. `vatRate` viaja como fracción decimal
 * (0.21 = 21%). La numeración (`number`) es secuencial por año y se reserva
 * atómicamente en el servidor con `ExpenseCounter`.
 *
 * La categoría es el `code` de una tipología del catálogo configurable
 * `ExpenseCategoryDef` (Ajustes → Tipologías de gasto). Se guarda como texto
 * libre (sin enum de BBDD) y el frontend solo deja elegir las del catálogo.
 */
export const ExpenseStatusSchema = z.enum(['PENDIENTE', 'PAGADO', 'ANULADO']);
/**
 * QUÉ PAPEL respalda el gasto. Manda en dónde se declara: solo las facturas van
 * al libro registro de facturas recibidas y al 347. El kilometraje y las dietas
 * son asignaciones a una persona —se informan en el 190— y además no admiten
 * las operaciones pensadas para un proveedor (reasignar el pagador, pagar).
 */
export const ExpenseKindSchema = z.enum(['FACTURA_RECIBIDA', 'KILOMETRAJE', 'DIETA']);
export const ExpenseStatusLabel = {
    PENDIENTE: 'Pendiente',
    PAGADO: 'Pagado',
    ANULADO: 'Anulado',
};
/** Código de tipología del catálogo `ExpenseCategoryDef`. */
export const ExpenseCategorySchema = z.string().min(1).max(64);
/** Etiquetas legadas (enum antiguo) como fallback si un código no está en el
 *  catálogo. El origen de verdad es ahora `ExpenseCategoryDef`. */
export const ExpenseCategoryLabel = {
    suscripcion: 'Suscripción',
    subcontratacion: 'Subcontratación',
    oficina: 'Oficina',
    desplazamiento: 'Desplazamiento',
    marketing: 'Marketing',
    profesionales: 'Profesionales',
    otros: 'Otros',
};
// ---------- DTOs ----------
/**
 * Una base y su cuota para un tipo de IVA. `vatCents` es opcional al crear: si
 * no viene se calcula redondeando base × tipo, que es lo que hace cualquier
 * ticket. Se admite enviarlo para respetar al céntimo lo que diga el papel
 * cuando el redondeo del emisor no coincide con el nuestro.
 */
export const ExpenseVatLineInputSchema = z.object({
    vatRate: z.number().min(0).max(1),
    // No negativo: un gasto no tiene bases en negativo, y una base a 0 colada en
    // una hoja llegaba hasta el asiento —que la rechaza— con el gasto YA creado.
    subtotalCents: z.number().int().min(0),
    vatCents: z.number().int().min(0).optional(),
});
/**
 * SITUACIÓN DE TESORERÍA del gasto: no "¿se ha pagado?" sino "¿de dónde salió el
 * dinero y queda algo por hacer?".
 *
 * El estado de siempre (PENDIENTE/PAGADO/ANULADO) dice la verdad frente al
 * PROVEEDOR. Esto dice la verdad frente a la CAJA de JR2, que es otra cosa: un
 * gasto que adelantó un socio de su bolsillo está pagado para el proveedor y
 * sigue debiéndose desde la empresa.
 *
 * Se CALCULA, no se guarda. Un campo almacenado podría acabar diciendo
 * "reembolsado" mientras la cuenta 551 dice que no; derivándolo, la pantalla no
 * puede contradecir a los libros.
 */
export const TreasuryStatusSchema = z.enum([
    /** Nadie ha pagado todavía. */
    'SIN_PAGAR',
    /** Se pagó una parte y no hay nada pendiente de devolver a nadie. */
    'PARCIAL',
    /** Lo adelantó una persona y la empresa aún se lo debe. */
    'ADELANTADO_PENDIENTE',
    /** Lo adelantó una persona y ya se le ha devuelto. */
    'ADELANTADO_REEMBOLSADO',
    /** Pagó la empresa, pero el movimiento aún no está cuadrado con el extracto. */
    'PAGADO_JR2_SIN_CUADRAR',
    /** Pagó la empresa y está conciliado con el banco. */
    'PAGADO_JR2',
    'ANULADO',
]);
export const ExpenseVatLineDtoSchema = z.object({
    vatRate: z.number(),
    subtotalCents: z.number().int(),
    vatCents: z.number().int(),
});
export const ExpenseDtoSchema = z.object({
    id: z.string(),
    number: z.number().int(),
    year: z.number().int(),
    /** ISO datetime de la fecha de emisión del gasto (factura del proveedor). */
    expenseDate: z.string().datetime(),
    supplierName: z.string(),
    supplierNif: z.string().nullable(),
    /** Factura de proveedor, o asignación (kilometraje/dieta) a una persona. */
    kind: ExpenseKindSchema.default('FACTURA_RECIBIDA'),
    concept: z.string(),
    category: ExpenseCategorySchema,
    serviceLine: ServiceLineSchema.nullable(),
    opportunityId: z.string().nullable(),
    opportunityCode: z.string().nullable(),
    /** Fase de imputación del gasto de actividad (PREVENTA → CAC, EJECUCION → entrega). Null = gasto general. */
    phase: TimeEntryPhaseSchema.nullable().default(null),
    subtotalCents: z.number().int(),
    /** IVA como fracción (0.21 = 21%). */
    vatRate: z.number(),
    vatCents: z.number().int(),
    totalCents: z.number().int(),
    /** IRPF como fracción (0.15 = 15%); null si la factura del proveedor no retiene. */
    irpfRate: z.number().nullable().default(null),
    /** Retención IRPF en céntimos. La deuda pagadera al proveedor = totalCents − irpfCents. */
    irpfCents: z.number().int().default(0),
    /** Acumulado ya pagado al proveedor (céntimos). */
    paidCents: z.number().int().default(0),
    /**
     * Quién puso el dinero y cuánto. Vacío = lo pagó la empresa desde su banco.
     * Varias filas cuando se pagó a medias (el caso de los dos socios).
     */
    payers: z
        .array(z.object({
        /** Id de la fila de reparto: es lo que se marca al liquidar. */
        id: z.string().default(''),
        userId: z.string().nullable(),
        userName: z.string().nullable(),
        amountCents: z.number().int(),
        /** Ya devuelto por transferencia. Solo aplica a personas, no a JR2. */
        reimbursedCents: z.number().int().default(0),
    }))
        .default([]),
    /** Situación de tesorería, calculada. Ver TreasuryStatusSchema. */
    treasuryStatus: TreasuryStatusSchema.default('SIN_PAGAR'),
    /** Lo que la empresa aún debe a personas por este gasto. */
    pendingReimbursementCents: z.number().int().default(0),
    status: ExpenseStatusSchema,
    paidAt: z.string().datetime().nullable(),
    createdAt: z.string().datetime(),
    createdByName: z.string().nullable(),
    /** F41/F43 — Documento justificante (factura recibida OCR), si lo hay. */
    documentId: z.string().nullable().default(null),
    /**
     * Desglose del IVA por tipo. Siempre trae al menos una línea; varias cuando
     * el papel mezcla tipos. `.default([])` para que un cliente viejo no reviente.
     */
    vatLines: z.array(ExpenseVatLineDtoSchema).default([]),
});
// ---------- Inputs ----------
export const CreateExpenseSchema = z.object({
    expenseDate: z.string().datetime(),
    supplierName: z.string().min(1).max(200),
    supplierNif: z.string().max(40).nullable().optional(),
    concept: z.string().min(1).max(500),
    category: ExpenseCategorySchema,
    serviceLine: ServiceLineSchema.nullable().optional(),
    opportunityId: z.string().nullable().optional(),
    /** Fase de imputación (solo tiene sentido con opportunityId). */
    phase: TimeEntryPhaseSchema.nullable().optional(),
    subtotalCents: z.number().int().min(0),
    /** Fracción 0..1. Por defecto 0.21 si no se envía. */
    vatRate: z.number().min(0).max(1).optional(),
    /**
     * Desglose por tipo de IVA. Si se envía MANDA: `subtotalCents` y `vatRate`
     * de arriba se ignoran y los totales del gasto salen de sumar estas líneas.
     * Un ticket de súper mezcla 10 % y 21 %, y meterlo con un tipo medio deduce
     * mal y ensucia el libro registro.
     */
    vatLines: z.array(ExpenseVatLineInputSchema).min(1).max(6).optional(),
});
export const UpdateExpenseSchema = CreateExpenseSchema.partial();
export const PayExpenseSchema = z.object({
    /**
     * Importe a pagar (céntimos). OPCIONAL: si se omite, el servidor liquida la
     * deuda pagadera pendiente (totalCents − irpfCents − paidCents) → pago total.
     * Si se envía, se valida contra ese pendiente (rechaza sobrepago). La
     * retención IRPF NO se paga al proveedor (se liquida vía modelo 111).
     */
    amountCents: z.number().int().min(1).optional(),
    paymentDate: z.string().datetime(),
    /**
     * Reparto de quién pone el dinero. `userId` a null = la empresa desde su
     * banco. Si se omite, se comporta como siempre: paga la empresa.
     *
     * La suma tiene que cuadrar EXACTAMENTE con el importe que se paga; el
     * servidor lo comprueba y rechaza el pago si no. Un reparto que no cuadre
     * dejaría el asiento descuadrado o inventaría deuda con un socio.
     */
    payers: z
        .array(z.object({
        userId: z.string().nullable(),
        amountCents: z.number().int().positive(),
    }))
        .min(1)
        .max(8)
        .optional(),
});
// ---------- Listado ----------
export const ExpenseListQuerySchema = z.object({
    year: z.coerce.number().int().optional(),
    status: ExpenseStatusSchema.optional(),
    /** Filtra los gastos vinculados a una oportunidad (panel de la ficha). */
    opportunityId: z.string().optional(),
    q: z.string().optional(),
    /**
     * Filtra por QUIÉN puso el dinero. Un id de usuario, o la palabra `company`
     * para los que pagó la empresa desde el banco —que incluye los gastos sin
     * reparto, los de antes de que existiera este concepto—.
     */
    paidBy: z.string().optional(),
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(200).default(50),
});
export const ExpenseListResponseSchema = z.object({
    items: z.array(ExpenseDtoSchema),
    total: z.number().int(),
    page: z.number().int(),
    pageSize: z.number().int(),
    /**
     * Cuánto ha adelantado cada persona en el AÑO consultado, sin el resto de
     * filtros: es el panel de "quién ha puesto qué", y filtrar por una persona no
     * debe hacer desaparecer a las demás de su propio resumen.
     */
    payerSummary: z
        .array(z.object({
        userId: z.string().nullable(),
        userName: z.string().nullable(),
        /** Lo adelantado en total por esa persona en el año consultado. */
        amountCents: z.number().int(),
        /**
         * Lo que se le sigue debiendo: adelantado MENOS reembolsado. Es la cifra
         * que manda en la ficha, porque es la única que dice qué hay que hacer.
         * Para JR2 (userId null) siempre es 0: la empresa no se debe a sí misma.
         */
        pendingCents: z.number().int().default(0),
        count: z.number().int(),
    }))
        .default([]),
});
/**
 * Corregir quién pagó un gasto YA PAGADO. La suma tiene que ser exactamente lo
 * ya pagado (`paidCents`): no se está pagando nada nuevo, se está diciendo de
 * quién salió el dinero que ya salió.
 */
export const ReassignPayersSchema = z.object({
    payers: z
        .array(z.object({
        userId: z.string().nullable(),
        amountCents: z.number().int().positive(),
    }))
        .min(1)
        .max(8),
});
//# sourceMappingURL=expenses.js.map